import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { Alert, Dialog, Switch, Button } from '@theguarantors/tg-package-material-ui';
import {
  Control,
  Controller,
  FieldErrors,
  useWatch,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupRadioButtonV2, {
  type RadioInputBox,
} from '../../../components/input/GroupRadioButtonV2';
import { GroupRadioButtonSkeleton } from '../../../components/input/GroupRadioButton';
import { theme } from '@theguarantors/tg-package-material-ui';
import { Renter } from '../../../utils/types/renter.interface';
import { staticInstitutionalScreeningResultsOptions } from '../ScreeningResults.options';
import { useSplitFeatureFlag } from '@/hooks/useSplitFeatureFlag';
import { SplitFeatureFlag } from '@/utils/split/splitConfig';
import { useCoverageRules } from '@/shared/hooks';
import { resolveInstitutionalCoverageOptions } from '../utils/coverage.util';
import { InfoBanner } from '@/components/InfoBanner';

interface HelpfulTipsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpfulTipsDialog = ({ isOpen, onClose }: HelpfulTipsDialogProps) => {
  return (
    <Dialog
      scroll="paper"
      open={isOpen}
      onClose={onClose}
      sx={{
        top: 'unset',
        '& .MuiBackdrop-root': {
          background: 'rgba(8, 10, 45, 0.4)',
        },
        '& .MuiDialogActions-root': {
          marginX: '16px !important',
        },
        '& .MuiPaper-root': {
          boxShadow: '4px 16px 32px 0px rgba(0, 0, 0, 0.08)',
          p: '32px',
          width: '508px',
          height: '442px',
          paddingX: '16px !important',
          [theme.breakpoints.down('md')]: {
            height: 'calc(100vh - 120px)',
            paddingX: '0px !important',
            paddingBottom: '16px !important',
          },
        },
        [theme.breakpoints.up('md')]: {
          top: '10px',
          marginLeft: '72px',
        },
      }}
      showCloseButton={false}
    >
      <DialogTitle sx={{ paddingX: '16px !important' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography
            variant="h5"
            component="h2"
            sx={{
              lineHeight: '36px',
              [theme.breakpoints.down('md')]: {
                fontSize: '24px',
              },
            }}
          >
            Tips from our team on selecting your coverage
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="Close helpful tips modal"
            sx={{ marginTop: '-8px', marginRight: '-8px' }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          borderTop: 'none',
          marginBottom: '16px !important',
          overflowX: 'wrap',
          overflowY: 'scroll',
          paddingRight: '8px',
          paddingBottom: '16px',
          paddingX: '16px !important',
          gap: 1,
          scrollbarColor: `${theme.palette.neutralGrey[300]} white`,
          borderBottom: `1px solid ${theme.palette.amber?.[200] || '#d7d1c4'}`,
          [theme.breakpoints.up('md')]: {
            border: 'none',
            marginRight: '-12px !important',
            paddingRight: '8px',
            height: 294,
          },
        }}
      >
        <Stack gap={1} sx={{ [theme.breakpoints.up('sm')]: { paddingRight: '8px' } }}>
          <Typography
            variant="body1"
            sx={{ lineHeight: '24px', fontWeight: 600, paddingTop: '8px' }}
          >
            Determining the right coverage length
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '8px' }}>
            Depending on your risk tolerance, our team recommends you to select the amount of
            coverage that protects you through your typical eviction timeline and how long it would
            take you to re-rent your property. You can use this coverage for months of rent loss at
            any point in the lease.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: '24px', fontWeight: 600 }}>
            Estimating the cost to your renter
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '8px' }}>
            Pricing varies based on a number of factors, but averages based on the months of
            coverage selected are:
            <ul
              style={{
                paddingLeft: '1rem',
                margin: 4,
                marginBottom: 8,
              }}
            >
              <li>
                <b style={{ fontWeight: 600 }}>3 months:</b> 75% of one month’s rent
              </li>
              <li>
                <b style={{ fontWeight: 600 }}>6 months:</b> 100% of one month’s rent
              </li>
              <li>
                <b style={{ fontWeight: 600 }}>12 months:</b> 115% of one month’s rent
              </li>
            </ul>
            Your renter pays for the coverage. If you would like to offset the cost for your renter
            and pay a portion or the total cost of coverage yourself, we recommend talking to your
            renter about reimbursement.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: '24px', fontWeight: 600 }}>
            What Rent Coverage includes
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '8px' }}>
            This covers losses due to missed rent payments, vacancy, skips, and no-shows.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: '24px', fontWeight: 600 }}>
            Adding optional Deposit Coverage
          </Typography>
          <Typography variant="body1">
            You have the option to allocate up to 1 month of coverage towards deposit related
            losses. Including Deposit Coverage will raise the renter cost of coverage by 30% on
            average and will reduce their chances of being approved after submitting an application
            with us.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" size="large" onClick={onClose} fullWidth>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ScreeningResultsProps {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  watch: UseFormWatch<Renter>;
  setValue: UseFormSetValue<Renter>;
  isSmbInvites: boolean;
  coverageLengthOptions: RadioInputBox[];
  useDrawerStyles?: boolean;
}

const ScreeningResults = ({
  control,
  errors,
  watch,
  setValue,
  isSmbInvites,
  coverageLengthOptions,
  useDrawerStyles = false,
}: ScreeningResultsProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const customCoverage = watch('customCoverage');

  const includeDepositCoverage = watch('includeDepositCoverage');

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Dynamic coverage rules logic for institutional (matches ScreeningResults.tsx)
  const { isFeatureFlagEnabled: isDynamicCoveragesFeatureFlagEnabled } = useSplitFeatureFlag(
    SplitFeatureFlag.OTTERS_4472_DYNAMIC_COVERAGES_INVITE,
  );

  const address = useWatch({ control, name: 'address' });
  const screeningResult = useWatch({ control, name: 'approvalStatus' });

  const addressSelected = Boolean(address?.monolith_building_id);

  const isDynamicCoverageRulesEnabled = Boolean(
    isDynamicCoveragesFeatureFlagEnabled && address?.property_id && !isSmbInvites,
  );

  const { data: coverageRulesFromApi, isLoading } = useCoverageRules(
    address?.property_id ?? '',
    isDynamicCoverageRulesEnabled,
  );

  const options = useMemo(
    () =>
      resolveInstitutionalCoverageOptions(
        staticInstitutionalScreeningResultsOptions,
        coverageRulesFromApi,
      ),
    [coverageRulesFromApi],
  );

  useEffect(() => {
    if (
      !isSmbInvites &&
      !isLoading &&
      screeningResult &&
      options.some((opt) => opt.value === screeningResult && opt.disabled)
    ) {
      setValue('approvalStatus', '', { shouldValidate: true });
    }
  }, [isSmbInvites, isLoading, screeningResult, options, setValue]);

  const showSkeleton = isLoading && !isSmbInvites && addressSelected;
  const isLocked = isDynamicCoveragesFeatureFlagEnabled && !addressSelected && !isSmbInvites;

  return (
    <>
      {isSmbInvites && (
        <>
          <Typography
            variant={useDrawerStyles ? 'h6' : 'h5'}
            component="h2"
            mb={1}
            {...(useDrawerStyles && { fontWeight: 'bold !important', lineHeight: '28px' })}
          >
            Select your maximum coverage limit
          </Typography>
          <Typography
            mb={useDrawerStyles ? 2 : 1}
            {...(useDrawerStyles
              ? {
                  variant: 'body2',
                  fontWeight: '350',
                  letterSpacing: 0,
                  sx: {
                    fontSize: '16px',
                    [theme.breakpoints.down('md')]: {
                      fontSize: '14px',
                      lineHeight: '22px',
                    },
                  },
                }
              : { sx: { color: `${theme.palette.neutralGrey[700]} !important` } })}
          >
            This corresponds to the maximum amount of rent loss you can claim. For example, if you
            select 3 months of coverage and your rent is $2,000, you can claim up to $6,000 of rent
            loss.
          </Typography>
          <Typography
            mb={4}
            {...(useDrawerStyles
              ? {
                  variant: 'body2',
                  fontWeight: '350',
                  letterSpacing: 0,
                  sx: {
                    fontSize: '16px',
                    [theme.breakpoints.down('md')]: {
                      fontSize: '14px',
                      lineHeight: '22px',
                    },
                  },
                }
              : { sx: { color: `${theme.palette.neutralGrey[700]} !important` } })}
          >
            If you need help deciding,{' '}
            <Link
              onClick={openModal}
              sx={{
                textTransform: 'none',
                minWidth: 'auto',
                cursor: 'pointer',
                borderBottom: `2px solid ${theme.palette.midnightGrey[700]}`,
                '&:hover': {
                  color: `${theme.palette.midnightGrey[950]} !important`,
                  borderColor: `${theme.palette.midnightGrey[950]} !important`,
                },
              }}
            >
              review our helpful tips.
            </Link>
          </Typography>
          <Box mt={2} mb={useDrawerStyles ? 4 : 0}>
            <Controller
              control={control}
              name="monthsOfCoverage"
              render={({ field }) => (
                <GroupRadioButtonV2
                  id="monthsOfCoverage"
                  dataTestId="monthsOfCoverage"
                  options={coverageLengthOptions}
                  field={field}
                  disabled={!!customCoverage}
                  useDrawerStyles={useDrawerStyles}
                />
              )}
            />
            {errors.monthsOfCoverage && (
              <FormHelperText error={true}>{errors.monthsOfCoverage.message}</FormHelperText>
            )}
          </Box>
        </>
      )}

      {!isSmbInvites && (
        <>
          <Typography
            variant={useDrawerStyles ? 'h6' : 'h5'}
            component="h2"
            mb={1}
            {...(useDrawerStyles && { fontWeight: 'bold !important', lineHeight: '28px' })}
          >
            Screening result
          </Typography>
          <Typography
            {...(useDrawerStyles
              ? {
                  variant: 'body2',
                  fontWeight: '350',
                  letterSpacing: 0,
                  sx: {
                    fontSize: '16px',
                    [theme.breakpoints.down('md')]: {
                      fontSize: '14px',
                      lineHeight: '22px',
                    },
                  },
                }
              : { sx: { color: `${theme.palette.neutralGrey[700]} !important` } })}
          >
            Based on your review of this renter at this time, are they approved, approved with
            conditions, or denied? This can be changed later, but it will impact the quoted premium.
          </Typography>
          {isLocked && (
            <InfoBanner icon={<LockOutlinedIcon fontSize="inherit" />} sx={{ mt: 1 }}>
              Please complete the lease information above first
            </InfoBanner>
          )}
          <Typography
            mt={4}
            variant="body2"
            fontWeight="350"
            mb="12px"
            letterSpacing={0}
            sx={{
              fontSize: '16px',
              [theme.breakpoints.down('md')]: {
                fontSize: '14px',
                lineHeight: '22px',
              },
            }}
          >
            Select one{' '}
            <Box component="span" sx={{ color: 'error.main' }}>
              *
            </Box>
          </Typography>
          {showSkeleton ? (
            <GroupRadioButtonSkeleton
              count={staticInstitutionalScreeningResultsOptions.length}
              dataTestId="screening-results-skeleton"
            />
          ) : (
            <Controller
              control={control}
              name="approvalStatus"
              render={({ field }) => (
                <GroupRadioButtonV2
                  id="approvalStatus"
                  dataTestId="approvalStatus"
                  options={options.map((opt) => ({
                    id: opt.id,
                    value: opt.value,
                    text: opt.text,
                    subText: opt.subText,
                    disabled: opt.disabled,
                    tooltip: opt.tooltip,
                    disabledTooltip: opt.disabledTooltip,
                  }))}
                  field={field}
                  disabled={isLocked}
                  useDrawerStyles={useDrawerStyles}
                />
              )}
            />
          )}
          {errors.approvalStatus && (
            <FormHelperText error={true}>{errors.approvalStatus.message}</FormHelperText>
          )}
        </>
      )}
      {isSmbInvites && (
        <>
          <Box mb={2} mt={1}>
            <Controller
              name="includeDepositCoverage"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value ? true : false} />}
                  label={
                    <Typography
                      sx={{
                        color: `${field.value ? theme.palette.midnightGrey[950] : '#888888'} !important`,
                        fontSize: '16px',
                        [theme.breakpoints.down('md')]: {
                          fontSize: '14px',
                          lineHeight: '22px',
                        },
                      }}
                    >
                      For an additional fee, add optional coverage to replace your cash security
                      deposit.
                    </Typography>
                  }
                />
              )}
            />
          </Box>
          <Box mb={4}>
            {includeDepositCoverage && (
              <Alert severity="warning">
                <Typography
                  variant="body2"
                  mb={1}
                  sx={{
                    fontSize: '16px',
                    [theme.breakpoints.down('md')]: {
                      fontSize: '14px',
                      lineHeight: '22px',
                    },
                  }}
                >
                  Please note the following if you select this option:
                </Typography>
                <Box component="ul" sx={{ margin: 0, paddingLeft: 3 }}>
                  <li>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '16px',
                        [theme.breakpoints.down('md')]: {
                          fontSize: '14px',
                          lineHeight: '22px',
                        },
                      }}
                    >
                      Deposit Coverage is intended for landlords who do not expect to hold a cash
                      security deposit
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '16px',
                        [theme.breakpoints.down('md')]: {
                          fontSize: '14px',
                          lineHeight: '22px',
                        },
                      }}
                    >
                      The added coverage may raise renter's quote by up to 30%
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '16px',
                        [theme.breakpoints.down('md')]: {
                          fontSize: '14px',
                          lineHeight: '22px',
                        },
                      }}
                    >
                      The likelihood of a renter being approved may be lower
                    </Typography>
                  </li>
                </Box>
              </Alert>
            )}
          </Box>
        </>
      )}

      <HelpfulTipsDialog isOpen={isModalOpen} onClose={closeModal} />
    </>
  );
};

export default ScreeningResults;
